import { z } from 'zod';

import { defaultNullish, mongoIdSchema } from './shared';

export enum ProductStatus {
  Ok = 'ok',

  Generating = 'generating', // ai
  GenerationError = 'generationError', // ai failed

  Locked = 'locked', // can be used by admin to block the user from accessing
  Removed = 'removed', // we might want to keep in store the removed
}

export enum ProductDisplayStatus {
  ToReview = 'toReview',
  Approved = 'approved',
}

export const productCreateSchema = z.object({
  primaryKeyword: z.string().trim(),
  title: z.string().trim(),

  uniqueIdentifier: defaultNullish(z.string().trim().default('')),
  productBrandName: defaultNullish(z.string().trim().default('')),
  price: defaultNullish(z.string().trim().default('')),
  allowedToScrapeSupplier: defaultNullish(z.boolean().default(true)),
  websiteUrl: defaultNullish(z.string().trim().default('')),
  extraInformation: defaultNullish(z.string().trim().default('')),
  pageType: defaultNullish(z.string().trim().default('')),
  requestedNumberOfWords: defaultNullish(z.number().default(0)),

  // TODO: should have a better rule for language
  language: z.string().trim(),
  country: z.string().trim(),

  brandId: mongoIdSchema,
  organizationId: mongoIdSchema,
  templateId: mongoIdSchema.nullish(),
  bulkGroupId: mongoIdSchema.nullish(),

  rawImportCsvData: z.record(z.unknown()).nullish(),

  status: z.nativeEnum(ProductStatus),
  displayStatus: defaultNullish(z.nativeEnum(ProductDisplayStatus).nullish()).default(
    ProductDisplayStatus.ToReview
  ),

  createdBy: mongoIdSchema,
});
export type ProductCreate = z.infer<typeof productCreateSchema>;

export const productSchema = z
  .object({
    _id: mongoIdSchema,
    isArchived: z.boolean().nullish(),
    archivedDate: z.number().nullish(),
    createdAt: z.number(),
    updatedAt: z.number(),
  })
  .merge(productCreateSchema);

export type Product = z.infer<typeof productSchema>;

export type CsvProductRow = {
  'Unique identifier'?: string;
  'Number of words'?: string;
  Brand?: string;
  'Product Title'?: string;
  Price?: string;
  'Page type'?: string;
  'Allowed to scrape supplier'?: string;
  'Seo keyword'?: string;
  'Files: images or PDF'?: string;
  'Website url'?: string;
  'Extra information'?: string;
};

export type CsvProductExportRow = {
  'Unique identifier': string;
  'Product Title': string;
  Errors: string;
  [key: string]: string;
};

export type CsvProductData = {
  row: number;
  uniqueIdentifier: string;
  title: ProductCreate['title'];
  primaryKeyword: ProductCreate['primaryKeyword'];
  brandName: string;
  price: string;
  filesUrls: string[];
  allowedToScrapeSupplier: boolean;
  websiteUrl: string;
  extraInformation: string;
  productType: string;
  numberOfWords: number;
};

export type CsvProductErr = {
  row: number;
  uniqueIdentifier: string;
  title: string;
  error: Error;
};
